// src/hooks/useBrowserNavigationBlocker.ts
// Enhanced Browser Navigation Blocker Hook
// Prevents back/forward browser navigation while allowing refresh
// Works across subdomains and page reloads

import { useEffect } from 'react';

/**
 * Comprehensive browser navigation blocker
 * Prevents back/forward navigation while allowing refresh
 * Works across subdomains and page reloads
 */
export const useBrowserNavigationBlocker = () => {
  useEffect(() => {
    const BLOCKER_KEY = 'nav_blocker_active';
    const HISTORY_STATE_KEY = 'navigation_blocked';
    
    // Initialize blocker
    const initializeBlocker = () => {
      // Create a unique state identifier
      const stateId = Date.now().toString();
      
      // Push multiple history states to create a "buffer"
      // This makes it harder for back button to escape our control
      for (let i = 0; i < 3; i++) {
        window.history.pushState(
          { [HISTORY_STATE_KEY]: true, id: stateId, index: i },
          '',
          window.location.href
        );
      }
      
      sessionStorage.setItem(BLOCKER_KEY, 'true');
      console.log('🚫 Browser navigation blocker initialized with buffer');
    };

    // Block navigation attempts
    const blockNavigation = (event: PopStateEvent) => {
      event.preventDefault();
      event.stopPropagation();
      
      // Immediately push new state to prevent navigation
      window.history.pushState(
        { [HISTORY_STATE_KEY]: true, id: Date.now() },
        '',
        window.location.href
      );
      
      console.log('🚫 Browser navigation blocked');
      
      // Optional: Show user feedback
      showNavigationBlockedNotification();
    };

    // Show subtle notification to user
    const showNavigationBlockedNotification = () => {
      // Check if notification already exists
      if (document.getElementById('nav-block-notification')) return;
      
      const notification = document.createElement('div');
      notification.id = 'nav-block-notification';
      notification.textContent = 'Please use in-app navigation';
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #1f2937;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        z-index: 999999;
        font-size: 14px;
        font-family: system-ui, -apple-system, sans-serif;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        animation: slideIn 0.3s ease-out;
      `;
      
      // Add animation
      const style = document.createElement('style');
      style.textContent = `
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `;
      document.head.appendChild(style);
      
      document.body.appendChild(notification);
      
      setTimeout(() => {
        notification.style.transition = 'opacity 0.3s ease-out';
        notification.style.opacity = '0';
        setTimeout(() => {
          notification.remove();
          style.remove();
        }, 300);
      }, 2000);
    };

    // Handle page restoration from cache (bfcache)
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        console.log('🔄 Page restored from bfcache, re-initializing blocker');
        initializeBlocker();
      }
    };

    // Handle page visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const isActive = sessionStorage.getItem(BLOCKER_KEY);
        if (isActive === 'true') {
          // Re-establish blocker when page becomes visible
          window.history.pushState(
            { [HISTORY_STATE_KEY]: true, id: Date.now() },
            '',
            window.location.href
          );
        }
      }
    };

    // Prevent navigation via keyboard shortcuts
    const handleKeyDown = (event: KeyboardEvent) => {
      // Block Alt+Left (back) and Alt+Right (forward)
      if (event.altKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
        event.preventDefault();
        event.stopPropagation();
        console.log('🚫 Keyboard navigation blocked');
        showNavigationBlockedNotification();
        return false;
      }
      
      // Block Backspace (when not in input)
      if (event.key === 'Backspace') {
        const target = event.target as HTMLElement;
        const isInput = target.tagName === 'INPUT' || 
                       target.tagName === 'TEXTAREA' || 
                       target.isContentEditable;
        
        if (!isInput) {
          event.preventDefault();
          console.log('🚫 Backspace navigation blocked');
        }
      }
    };

    // Handle before unload
    const handleBeforeUnload = () => {
      // Maintain blocker flag across page loads
      sessionStorage.setItem(BLOCKER_KEY, 'true');
    };

    // Check if blocker should be active
    const isActive = sessionStorage.getItem(BLOCKER_KEY);
    
    if (isActive === 'true') {
      // Re-initialize if returning from subdomain
      initializeBlocker();
    } else {
      // First time initialization
      initializeBlocker();
    }

    // Add all event listeners
    window.addEventListener('popstate', blockNavigation);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('keydown', handleKeyDown);

    // Handle hash changes (prevent fragment navigation)
    const handleHashChange = (event: HashChangeEvent) => {
      event.preventDefault();
      console.log('🚫 Hash navigation blocked');
    };
    window.addEventListener('hashchange', handleHashChange);

    // Cleanup function
    return () => {
      window.removeEventListener('popstate', blockNavigation);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('hashchange', handleHashChange);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Listen for logout events to clear blocker
  useEffect(() => {
    const handleLogout = () => {
      console.log('🧹 Clearing navigation blocker on logout');
      sessionStorage.removeItem('nav_blocker_active');
    };

    window.addEventListener('user_logout', handleLogout);
    
    return () => {
      window.removeEventListener('user_logout', handleLogout);
    };
  }, []);
};

// Export utility functions for manual control if needed
export const NavigationBlockerUtils = {
  enable: () => {
    sessionStorage.setItem('nav_blocker_active', 'true');
    for (let i = 0; i < 3; i++) {
      window.history.pushState(
        { navigation_blocked: true, id: Date.now(), index: i },
        '',
        window.location.href
      );
    }
    console.log('✅ Navigation blocker enabled');
  },
  
  disable: () => {
    sessionStorage.removeItem('nav_blocker_active');
    console.log('✅ Navigation blocker disabled');
  },
  
  isEnabled: () => {
    return sessionStorage.getItem('nav_blocker_active') === 'true';
  }
};